/**
 * 牌的编解码。见 docs/ARCHITECTURE.md §3.2。
 *
 * 全系统有两种牌表示：引擎与胜率热路径用 `CardId`（0..51 的整数，可做位运算与查表），
 * 协议 / DB / 日志用 `Card`（两字符串，如 "As"）。本模块是两者之间的**唯一**转换出口——
 * 散落的 `rank + suit` 字符串拼接一旦出现口径分歧（大小写、"10" vs "T"），
 * DB 里就会同时存在 "10s" / "Ts" / "TS" 三种写法，而且没人能发现。
 *
 * 编码 `cardId = rank * 4 + suit`：同点数的四张牌连续，于是 `rankOf` 是一次移位、
 * `suitOf` 是一次与运算，评估器可以直接用 CardId 做位图。
 */
import type { Card, CardId } from '../ids.js'
import type { DeckVariant } from './types.js'

/** 0..12 → 2,3,...,T,J,Q,K,A */
export type RankIndex = number
/** 0..3 → c,d,h,s */
export type SuitIndex = number

export const RANK_CHARS = '23456789TJQKA' as const
export const SUIT_CHARS = 'cdhs' as const
export const NUM_RANKS = 13
export const NUM_SUITS = 4
export const NUM_CARDS = 52

/** 非法牌面/牌 id。属于「调用方写错了」而非「外部输入不合法」，故走异常而非 EngineResult。 */
export class InvalidCardError extends Error {
  constructor(value: unknown) {
    super(`Invalid card: ${JSON.stringify(value)}`)
    this.name = 'InvalidCardError'
  }
}

/** 冻结的 [0..51]，洗牌的唯一牌源 */
export const FULL_DECK: readonly CardId[] = Object.freeze(
  Array.from({ length: NUM_CARDS }, (_unused, i) => i as CardId),
)

export function makeCard(rank: RankIndex, suit: SuitIndex): CardId {
  return rank * 4 + suit
}

export const rankOf = (c: CardId): RankIndex => c >> 2
export const suitOf = (c: CardId): SuitIndex => c & 3

export function isValidCardId(n: number): boolean {
  return Number.isInteger(n) && n >= 0 && n < NUM_CARDS
}

/** 短牌最小的点数（6）。2-5 的牌不在短牌牌堆里 */
export const SHORT_DECK_MIN_RANK = 4

/** 冻结的短牌牌堆：6 到 A，共 36 张 */
export const SHORT_DECK: readonly CardId[] = Object.freeze(
  FULL_DECK.filter((c) => c >> 2 >= SHORT_DECK_MIN_RANK),
)

/** 短牌里不存在的 16 张牌（2-5），算胜率时当作已知出局的牌 */
export const SHORT_DECK_MISSING: readonly CardId[] = Object.freeze(
  FULL_DECK.filter((c) => c >> 2 < SHORT_DECK_MIN_RANK),
)

/** 某个玩法的完整牌堆（洗牌前的牌源） */
export function deckOf(variant: DeckVariant): readonly CardId[] {
  return variant === 'shortDeck' ? SHORT_DECK : FULL_DECK
}

/** 这张牌在该玩法里是否存在 */
export function isValidCardIdFor(n: number, variant: DeckVariant): boolean {
  return isValidCardId(n) && (variant !== 'shortDeck' || n >> 2 >= SHORT_DECK_MIN_RANK)
}

// —— O(1) 查表 ——
// 序列化查表：避免每次 toCard 都做字符串拼接（胜率蒙特卡洛里这是百万次级调用）。
// 解析查表：按 charCode 直接索引，既是 O(1) 又不必为大小写不敏感先 toLowerCase 分配临时串。

const CHAR_TABLE_SIZE = 128

function buildCharTable(chars: string): Int8Array {
  const table = new Int8Array(CHAR_TABLE_SIZE).fill(-1)
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i]!
    table[ch.toUpperCase().charCodeAt(0)] = i
    table[ch.toLowerCase().charCodeAt(0)] = i
  }
  return table
}

const RANK_BY_CHARCODE = buildCharTable(RANK_CHARS)
const SUIT_BY_CHARCODE = buildCharTable(SUIT_CHARS)

/** 序列化恒为「大写点数 + 小写花色」 */
const CARD_STRINGS: readonly Card[] = Object.freeze(
  Array.from({ length: NUM_CARDS }, (_unused, c) => RANK_CHARS[c >> 2]! + SUIT_CHARS[c & 3]!),
)

/** 51 → "As"。非法 id 抛 InvalidCardError。 */
export function toCard(c: CardId): Card {
  const s = CARD_STRINGS[c]
  if (s === undefined) throw new InvalidCardError(c)
  return s
}

/**
 * "As" → 51。点数与花色字符**大小写均不敏感**；`10` 不接受，只接受 `T`。
 * 非法输入抛 InvalidCardError；需要容错解析请用 `tryToCardId`。
 */
export function toCardId(s: Card): CardId {
  const id = tryToCardId(s)
  if (id === null) throw new InvalidCardError(s)
  return id
}

export function tryToCardId(s: string): CardId | null {
  if (typeof s !== 'string' || s.length !== 2) return null
  const rc = s.charCodeAt(0)
  const sc = s.charCodeAt(1)
  if (rc >= CHAR_TABLE_SIZE || sc >= CHAR_TABLE_SIZE) return null
  const rank = RANK_BY_CHARCODE[rc] ?? -1
  const suit = SUIT_BY_CHARCODE[sc] ?? -1
  if (rank < 0 || suit < 0) return null
  return rank * 4 + suit
}

export function toCards(cs: readonly CardId[]): Card[] {
  const out: Card[] = new Array<Card>(cs.length)
  for (let i = 0; i < cs.length; i++) out[i] = toCard(cs[i]!)
  return out
}

export function toCardIds(cs: readonly Card[]): CardId[] {
  const out: CardId[] = new Array<CardId>(cs.length)
  for (let i = 0; i < cs.length; i++) out[i] = toCardId(cs[i]!)
  return out
}

/** 紧凑串，用于 DB 的 board / hole_cards 列："AsKd7h" */
export function packCards(cs: readonly CardId[]): string {
  let out = ''
  for (let i = 0; i < cs.length; i++) out += toCard(cs[i]!)
  return out
}

export function unpackCards(s: string): CardId[] {
  if (typeof s !== 'string' || s.length % 2 !== 0) throw new InvalidCardError(s)
  const out: CardId[] = new Array<CardId>(s.length / 2)
  for (let i = 0; i < s.length; i += 2) {
    const id = tryToCardId(s.slice(i, i + 2))
    // 整串一起报错：只报出错的两字符会让调用方看不出是哪一列的数据坏了
    if (id === null) throw new InvalidCardError(s)
    out[i / 2] = id
  }
  return out
}

/**
 * 重复牌检测。52 张牌用两个 32 位位图，避免在校验路径上分配 Set。
 * 越界值不由本函数负责判定合法性（那是 isValidCardId 的事），但仍参与重复判断。
 */
export function hasDuplicates(cs: readonly CardId[]): boolean {
  let lo = 0
  let hi = 0
  let outOfRange: Set<number> | null = null
  for (let i = 0; i < cs.length; i++) {
    const c = cs[i]!
    if (isValidCardId(c)) {
      if (c < 32) {
        const bit = 1 << c
        if ((lo & bit) !== 0) return true
        lo |= bit
      } else {
        const bit = 1 << (c - 32)
        if ((hi & bit) !== 0) return true
        hi |= bit
      }
    } else {
      outOfRange ??= new Set<number>()
      if (outOfRange.has(c)) return true
      outOfRange.add(c)
    }
  }
  return false
}

/** 仅 UI 使用，不进引擎 */
export const SUIT_SYMBOLS = ['♣', '♦', '♥', '♠'] as const
